class JournalListing extends HTMLElement {
	connectedCallback() {
		this._bindLoadMore()
		this._bindFilters()
		window.addEventListener("popstate", this._onPopState.bind(this))
	}

	// ─── Load more ────────────────────────────────────────────────────────────

	_bindLoadMore() {
		this.btn = this.querySelector("[data-journal-loadmore]")
		if (!this.btn) return
		this.btn.addEventListener("click", this.loadMore.bind(this))
	}

	async loadMore() {
		const nextUrl = this.btn.dataset.nextPage
		const sectionId = this.dataset.sectionId

		const url = new URL(nextUrl, window.location.origin)
		url.searchParams.set("sections", sectionId)

		this.btn.disabled = true
		this.btn.setAttribute("aria-busy", "true")

		try {
			const doc = await this._fetchSection(url.toString())

			// Append new cards
			const newCards = doc.querySelector("[data-journal-cards]")
			const currentCards = this.querySelector("[data-journal-cards]")
			if (newCards && currentCards) {
				currentCards.insertAdjacentHTML("beforeend", newCards.innerHTML)
			}

			// Update counter
			const newCount = doc.querySelector("[data-journal-count]")
			const currentCount = this.querySelector("[data-journal-count]")
			if (newCount && currentCount) {
				currentCount.textContent = newCount.textContent
			}

			// Update or remove button
			const newBtn = doc.querySelector("[data-journal-loadmore]")
			if (newBtn) {
				this.btn.dataset.nextPage = newBtn.dataset.nextPage
				this.btn.disabled = false
				this.btn.removeAttribute("aria-busy")
			} else {
				this.btn.remove()
				this.btn = null
			}
		} catch (err) {
			console.error("[JournalListing] Load more failed:", err)
			this.btn.disabled = false
			this.btn.removeAttribute("aria-busy")
		}
	}

	// ─── Filters ──────────────────────────────────────────────────────────────

	_bindFilters() {
		this.querySelectorAll("[data-journal-filter]").forEach((btn) => {
			btn.addEventListener("click", () => {
				const tagUrl = btn.dataset.tagUrl
				const tagHandle = btn.dataset.tagHandle
				this._setActiveFilter(tagHandle)
				history.pushState({ tagUrl, tagHandle }, "", tagUrl)
				this._applyFilter(tagUrl)
			})
		})
	}

	_setActiveFilter(tagHandle) {
		this.querySelectorAll("[data-journal-filter]").forEach((btn) => {
			const isActive = btn.dataset.tagHandle === tagHandle
			if (isActive) {
				btn.setAttribute("data-current", "")
				btn.setAttribute("aria-current", "true")
			} else {
				btn.removeAttribute("data-current")
				btn.removeAttribute("aria-current")
			}
		})
	}

	async _applyFilter(tagUrl) {
		const sectionId = this.dataset.sectionId

		const url = new URL(tagUrl, window.location.origin)
		url.searchParams.set("sections", sectionId)

		try {
			const doc = await this._fetchSection(url.toString())

			// Replace dynamic content
			const newContent = doc.querySelector("[data-journal-content]")
			const currentContent = this.querySelector("[data-journal-content]")
			if (newContent && currentContent) {
				currentContent.replaceWith(newContent)
			}

			// Re-bind load more on the new content
			this._bindLoadMore()
		} catch (err) {
			console.error("[JournalListing] Filter failed:", err)
		}
	}

	// ─── Popstate (browser back/forward) ──────────────────────────────────────

	_onPopState(event) {
		const tagUrl = event.state?.tagUrl ?? window.location.pathname
		const tagHandle = event.state?.tagHandle ?? ""
		this._setActiveFilter(tagHandle)
		this._applyFilter(tagUrl)
	}

	// ─── Shared fetch helper ───────────────────────────────────────────────────

	async _fetchSection(urlString) {
		const response = await fetch(urlString)
		if (!response.ok) throw new Error(response.statusText)

		const data = await response.json()
		const html = data[this.dataset.sectionId]
		if (!html) throw new Error("[JournalListing] Section not found in response")

		return new DOMParser().parseFromString(html, "text/html")
	}
}

customElements.define("journal-listing", JournalListing)
